import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, Optional,
  Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Translatable, TranslationService} from '@ngmedax/translation';
import * as CodeMirror from 'codemirror';
import 'codemirror/mode/javascript/javascript';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {TemplateVariable, TEMPLATE_VARIABLE_GROUPS} from '../../froala/template-variables';
import {TemplateVariablePickerComponent} from '../template-variable-picker/template-variable-picker.component';
import {FormulaFragmentComponent} from './formula-fragment.component';
import {FORMULA_FUNCTIONS, inspectFormula, FormulaPart, formulaVariableCode, formulaVariableLabel, writeFormulaPart} from './formula-language';

export interface FormulaInputComponent extends Translatable {}

/** Adds lossless, source-backed editing assistance to a JavaScript field. */
@Component({
  selector: 'app-qa-formula-input', templateUrl: './formula-input.component.html',
  styleUrls: ['./formula-input.component.css'], encapsulation: ViewEncapsulation.None
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class FormulaInputComponent implements AfterViewInit, OnChanges, OnDestroy {
  private static nextDialog = 0;
  @Input() value = '';
  @Input() variables: TemplateVariable[] = [];
  @Input() label = KEYS.EDITOR.FORMULA;
  @Input() compact = false;
  @Input() errorRange: {start: number; end: number} = null;
  @Output() valueChange = new EventEmitter<string>();
  @Output() saveRequested = new EventEmitter<void>();
  @ViewChild('source') source: ElementRef<HTMLTextAreaElement>;
  public assisted = true;
  public fallback = false;
  public nativeSource = false;
  public functions = FORMULA_FUNCTIONS;
  public operators = ['+', '-', '*', '/', '%', '**', '===', '!==', '>', '>=', '<', '<=', '&&', '||', '??', '&', '|', '^', '<<', '>>', '>>>'];
  public constants = ['Math.PI', 'Math.E', 'Math.LN2', 'Math.LN10', 'Math.LOG2E', 'Math.LOG10E', 'Math.SQRT2', 'Math.SQRT1_2'];
  private editor: any;
  private marks: any[] = [];
  private updating = false;
  private timer: ReturnType<typeof setTimeout>;
  private observer: ResizeObserver;
  private destroyed = false;
  private prefersAssistance = true;

  /** Keeps editor events within the owning component and opens reusable insertion dialogs. */
  public constructor(private modal: NgbModal, private zone: NgZone, @Optional() private translationService: TranslationService,
    @Optional() private changeDetector: ChangeDetectorRef) {}

  /** Initializes the source editor without emitting a synthetic user edit. */
  public ngAfterViewInit(): void {
    try {
      this.zone.runOutsideAngular(() => {
        this.editor = CodeMirror.fromTextArea(this.source.nativeElement, {
          value: this.value || '', mode: 'javascript', lineWrapping: true, viewportMargin: 20,
          indentUnit: 2, tabSize: 2, screenReaderLabel: this._(this.label)
        });
        this.editor.setValue(this.value || '');
        this.editor.on('changes', () => {
          if (this.updating) { return; }
          this.zone.run(() => {
            this.value = this.editor.getValue();
            this.valueChange.emit(this.value);
            this.scheduleDecorations();
          });
        });
        this.observer = new ResizeObserver(() => this.editor.refresh());
        this.observer.observe(this.editor.getWrapperElement());
      });
      this.decorate();
    } catch (error) { this.recoverSource(error); }
    this.changeDetector?.detectChanges();
  }

  /** Synchronizes external undo and form resets while retaining normal typing history. */
  public ngOnChanges(): void {
    if (!this.editor) {
      this.fallback = this.nativeSource || this.needsSource(inspectFormula(this.value || '').reason);
      this.assisted = this.prefersAssistance && !this.fallback;
      return;
    }
    try {
      if (this.editor.getValue() !== (this.value || '')) {
        this.updating = true;
        this.editor.setValue(this.value || '');
        this.editor.clearHistory();
        this.updating = false;
      }
      this.scheduleDecorations();
    } catch (error) { this.recoverSource(error); }
  }

  /** Releases timers, observers and native editor listeners when a formula disappears. */
  public ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.timer);
    this.observer?.disconnect();
    try { this.editor?.toTextArea(); } catch (error) { console.warn('Unable to release formula source editor.', error); }
  }

  /** Prevents formula shortcuts from reaching the questionnaire's global save handler. */
  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && (event.key.toLowerCase() === 's' || event.key === 'Enter')) {
      event.preventDefault();
      event.stopPropagation();
      this.saveRequested.emit();
    }
  }

  /** Changes presentation only, preserving source, selection and undo history. */
  public setMode(assisted: boolean): void {
    if (assisted && this.fallback) { return; }
    this.prefersAssistance = assisted;
    this.decorate();
    this.editor?.refresh();
  }

  /** Keeps a native text field usable even if the enhanced editor cannot initialize or render. */
  public onNativeInput(event: Event): void {
    this.value = (event.target as HTMLTextAreaElement).value;
    this.valueChange.emit(this.value);
  }

  /** Falls back to plain text without replacing, parsing or normalizing the authored source. */
  private recoverSource(error: unknown): void {
    console.warn('Formula assistance unavailable; preserving the formula in the source field.', error);
    this.observer?.disconnect();
    clearTimeout(this.timer);
    const wrapper = this.editor?.getWrapperElement();
    try { this.editor?.toTextArea(); } catch (_) { wrapper?.remove(); }
    this.editor = null;
    this.marks = [];
    this.nativeSource = true;
    this.fallback = true;
    this.assisted = false;
    if (this.source) {
      this.source.nativeElement.style.display = '';
      this.source.nativeElement.value = this.value || '';
    }
  }

  /**
   * Only constructs the assistant cannot represent force the source view. A syntax error is
   * usually an unfinished formula ("1 +"), so the toolbar stays available to complete it
   */
  private needsSource(reason: string): boolean {
    return !!reason && reason !== 'syntax';
  }

  /** Debounces syntax analysis while the author types incomplete expressions. */
  private scheduleDecorations(): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.decorate(), 80);
  }

  /** Renders inert labels as atomic widgets; the underlying document always contains JavaScript. */
  private decorate(): void {
    if (!this.editor || this.destroyed) { return; }
    try {
      const renderedSource = this.editor.getValue();
      const inspection = inspectFormula(renderedSource);
      this.fallback = this.needsSource(inspection.reason);
      this.assisted = this.prefersAssistance && !this.fallback;
      this.editor.setOption('lineNumbers', !this.assisted);
      this.editor.operation(() => {
        this.marks.forEach(mark => mark.clear());
        this.marks = [];
        if (this.errorRange) {
          this.marks.push(this.editor.markText(this.editor.posFromIndex(this.errorRange.start),
            this.editor.posFromIndex(this.errorRange.end), {className: 'qa-formula-error'}));
        }
        if (!this.assisted) { return; }
        inspection.parts.forEach(part => {
          const variable = this.variables.find(item => item.path === part.name || item.path === part.name.replace(/^patient\./, 'client.'));
          const definition = this.functions.find(item => `Math.${item.name}` === part.name);
          const label = variable ? formulaVariableLabel(variable) :
            definition ? this._(definition.label) : part.name === 'match' ? this._(KEYS.EDITOR.FORMULA_MATCH) :
              part.kind === 'condition' ? this._(part.name) : part.name;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `qa-formula-token qa-formula-token-${part.kind}`;
          // formulas used in this formula look different from answers and other variables
          variable && variable.group && button.classList.add(`qa-formula-token-group-${variable.group}`);
          if (this.errorRange && part.start < this.errorRange.end && part.end > this.errorRange.start) {
            button.classList.add('qa-formula-error');
          }
          button.textContent = label + (part.statement && part.name === 'Dann' ? ' {' : '');
          button.title = variable ? `${variable.group === 'formula' ? this._(KEYS.EDITOR.FORMULA_TOKEN_FORMULA) + ': ' : ''}` +
            `${variable.label}\n${part.name}` : this.editor.getValue().slice(part.from, part.to);
          button.setAttribute('aria-label', `${this._(KEYS.EDITOR.FORMULA_EDIT_PART)}: ${label} (${part.name})`);
          button.addEventListener('mousedown', event => event.preventDefault());
          button.addEventListener('click', () => this.zone.run(() => {
            if (!this.editor || this.editor.getValue() !== renderedSource) { this.decorate(); return; }
            this.editPart(part);
          }));
          this.marks.push(part.start === part.end ?
            this.editor.setBookmark(this.editor.posFromIndex(part.start), {widget: button, insertLeft: true}) :
            this.editor.markText(this.editor.posFromIndex(part.start), this.editor.posFromIndex(part.end),
              {replacedWith: button, handleMouseEvents: true, clearOnEnter: false}));
        });
      });
    } catch (error) { this.recoverSource(error); }
  }

  /** Opens a searchable catalog at the current source selection or an existing variable range. */
  public variable(part?: FormulaPart): void {
    const range = this.range(part);
    const ref = this.modal.open(TemplateVariablePickerComponent, {size: 'lg', centered: true,
      ariaLabelledBy: 'qa-template-variable-title'});
    const picker = ref.componentInstance as TemplateVariablePickerComponent;
    picker.variables = this.variables;
    picker.title = KEYS.EDITOR.DEPENDENCY_CHOOSE;
    picker.hint = KEYS.EDITOR.DEPENDENCY_SEARCH;
    picker.footer = KEYS.EDITOR.FORMULA_RUNTIME_HINT;
    picker.groups = [{id: 'answers', label: KEYS.EDITOR.DEPENDENCY_ANSWERS, icon: 'fa-check-square-o'}, ...TEMPLATE_VARIABLE_GROUPS];
    // a clicked variable is marked and scrolled into view
    part && part.kind === 'variable' && (picker.selected = this.variables.some(item => item.path === part.name) ?
      part.name : part.name.replace(/^patient\./, 'client.'));
    ref.result.then((value: TemplateVariable) => this.replace(range, formulaVariableCode(value.path)), () => this.focus());
  }

  /** Opens a populated function or conditional editor for the selected source node. */
  public editPart(part: FormulaPart): void {
    if (part.kind === 'variable') { this.variable(part); return; }
    if (part.kind === 'constant') {
      this.editor.setSelection(this.editor.posFromIndex(part.from), this.editor.posFromIndex(part.to));
      this.focus();
      return;
    }
    this.fragment(part, this.range(part));
  }

  /** Creates a function with editable arguments, using selected code as its first operand. */
  public addFunction(name: string): void {
    const definition = this.functions.find(item => `Math.${item.name}` === name);
    const selected = this.editor.getSelection().trim();
    const fields = name === 'match' ? [selected || 'client.firstName', '/./'] :
      Array.from({length: definition?.args || 0}, (_, index) => index === 0 && selected ? selected : '0');
    this.fragment({kind: 'function', name, fields, start: 0, end: 0, from: 0, to: 0}, this.range());
  }

  /** Inserts a conditional expression anywhere or an explicit if statement at the chosen position. */
  public condition(statement = false): void {
    this.fragment({kind: 'condition', name: 'Wenn', statement,
      fields: [this.editor.getSelection().trim() || 'true', '1', '0'], start: 0, end: 0, from: 0, to: 0}, this.range());
  }

  /** Edits a detached fragment; cancelling never changes the parent formula. */
  private fragment(part: FormulaPart, range: {from: any; to: any; source?: string}): void {
    const titleId = `qa-formula-fragment-${FormulaInputComponent.nextDialog++}`;
    const ref = this.modal.open(FormulaFragmentComponent, {size: 'lg', centered: true, scrollable: true, windowClass: 'qa-formula-modal',
      ariaLabelledBy: titleId});
    ref.componentInstance.titleId = titleId;
    ref.componentInstance.part = {...part, fields: [...(part.fields || [])]};
    ref.componentInstance.variables = this.variables;
    ref.result.then((result: FormulaPart) => {
      if (part.to > part.from && JSON.stringify(result.fields) === JSON.stringify(part.fields)) { this.focus(); return; }
      this.replace(range, writeFormulaPart(result));
    }, () => this.focus());
  }

  /** Inserts operators and literal snippets without inventing implicit arithmetic between variables. */
  public insert(code: string, wrap = false): void {
    const selected = this.editor.getSelection();
    this.replace(this.range(), wrap ? code.replace('$', selected || '0') : code);
  }

  /** Captures the editor selection before a modal receives focus. */
  private range(part?: FormulaPart): {from: any; to: any; source: string} {
    return {source: this.editor.getValue(), ...(part ?
      {from: this.editor.posFromIndex(part.from), to: this.editor.posFromIndex(part.to)} :
      {from: this.editor.getCursor('from'), to: this.editor.getCursor('to')})};
  }

  /** Applies a confirmed insertion as a single undoable edit. */
  private replace(range: {from: any; to: any; source?: string}, text: string): void {
    if (this.destroyed || !this.editor) { return; }
    const source = this.editor.getValue();
    if (range.source !== undefined && range.source !== source) {
      console.warn('Formula changed while its insertion dialog was open; the newer source is preserved.');
      this.focus();
      return;
    }
    const from = this.editor.indexFromPos(range.from);
    const to = this.editor.indexFromPos(range.to);
    if (/[\w$]$/.test(source.slice(0, from)) && /^[\w$]/.test(text)) { text = ' ' + text; }
    if (/[\w$]$/.test(text) && /^[\w$]/.test(source.slice(to))) { text += ' '; }
    this.editor.replaceRange(text, range.from, range.to, '+input');
    this.editor.setCursor(this.editor.posFromIndex(from + text.length));
    this.focus();
    this.decorate();
  }

  /** Restores keyboard input after closing an auxiliary dialog. */
  private focus(): void {
    if (!this.destroyed) { this.editor ? this.editor.focus() : this.source?.nativeElement.focus(); }
  }
}
